import { EventManagerMixin } from '../utils/event-manager.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { iconLibrary } from '../utils/icon-library.js';
import { CONFIG } from '../config.js';

/**
 * IconPicker - Modal dialog for selecting hex icons
 *
 * Uses EventManagerMixin for automatic listener cleanup
 * Uses HexEvents for consistent event dispatching
 *
 * Events emitted:
 * - HexEvents.ICON_SAVED: When an icon is saved (bubbles, composed)
 */
class IconPicker extends EventManagerMixin(HTMLElement) {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.selectedIcon = null;
    this.selectedColor = CONFIG.ICON_COLOR_PRESETS[3]; // Default blue (#3b82f6)
    this.selectedVisibility = CONFIG.VISIBILITY_SHOW; // Default to show
    this.hexCoord = null;
    this.iconData = null; // For edit mode
    this.editMode = false;

    // Icon categories will be loaded from manifest
    this.iconCategories = {};
    this.categoriesLoaded = false;

    // Search state
    this.searchQuery = '';
    this.searchResults = [];

    // Preset colors (from CONFIG)
    this.presetColors = CONFIG.ICON_COLOR_PRESETS;

    this.activeCategory = 'creatures';
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    // Clean up IntersectionObserver
    if (this.iconObserver) {
      this.iconObserver.disconnect();
      this.iconObserver = null;
    }
    // EventManagerMixin cleanup is handled automatically
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${CONFIG.COLORS.OVERLAY_DARKER};
          z-index: ${CONFIG.Z_INDEX_MODAL};
          align-items: center;
          justify-content: center;
        }

        :host([open]) {
          display: flex;
        }

        .dialog {
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 8px;
          max-width: 800px;
          width: 90%;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid #333;
        }

        .header h2 {
          margin: 0;
          color: #e5e5e5;
          font-size: 1.25rem;
        }

        .close-button {
          background: none;
          border: none;
          color: #a0a0a0;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          color: #e5e5e5;
        }

        .body {
          padding: 16px;
          overflow-y: auto;
          flex: 1;
        }

        .search-section {
          margin-bottom: 16px;
        }

        .search-input {
          width: 100%;
          background: #2a2a2a;
          border: 1px solid #444;
          color: #e5e5e5;
          padding: 10px 12px;
          border-radius: 4px;
          font-size: 0.875rem;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .search-input::placeholder {
          color: #666;
        }

        .search-results-label {
          color: #a0a0a0;
          font-size: 0.875rem;
          margin-top: 12px;
          margin-bottom: 8px;
          display: block;
        }

        .categories {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
          border-bottom: 1px solid #333;
          padding-bottom: 8px;
        }

        .categories.hidden {
          display: none;
        }

        .category-tab {
          background: none;
          border: none;
          color: #a0a0a0;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 0.875rem;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .category-tab:hover {
          color: #e5e5e5;
        }

        .category-tab.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        .icon-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
          gap: 8px;
          margin-bottom: 24px;
          max-height: 282px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .icon-item {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #2a2a2a;
          border: 2px solid transparent;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .icon-item:hover {
          background: #333;
          border-color: #444;
        }

        .icon-item.selected {
          background: #333;
          border-color: #3b82f6;
        }

        .icon-item i {
          font-size: 1.5rem;
          color: #e5e5e5;
        }

        .section-label {
          color: #a0a0a0;
          font-size: 0.875rem;
          margin-bottom: 8px;
          display: block;
        }

        .color-section {
          margin-bottom: 24px;
        }

        .color-grid {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .color-preset {
          width: 40px;
          height: 40px;
          border: 2px solid transparent;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .color-preset:hover {
          transform: scale(1.1);
        }

        .color-preset.selected {
          border-color: #fff;
        }

        .custom-color {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .custom-color input[type="text"] {
          background: #1f1f1f;
          border: 1px solid #333;
          color: #e5e5e5;
          padding: 8px;
          border-radius: 4px;
          font-family: monospace;
          width: 100px;
        }

        .custom-color input[type="color"] {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .visibility-section {
          margin-bottom: 24px;
        }

        .visibility-options {
          display: flex;
          gap: 16px;
        }

        .visibility-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .visibility-option input[type="radio"] {
          cursor: pointer;
        }

        .visibility-option label {
          cursor: pointer;
          color: #e5e5e5;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .visibility-badge {
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: bold;
        }

        .visibility-badge.show {
          background: #10b981;
          color: white;
        }

        .visibility-badge.secret {
          background: #ef4444;
          color: white;
        }

        .preview-section {
          margin-bottom: 24px;
          padding: 16px;
          background: #2a2a2a;
          border-radius: 4px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .preview-icon {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .preview-label {
          color: #a0a0a0;
          font-size: 0.875rem;
        }

        .footer {
          padding: 16px;
          border-top: 1px solid #333;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .btn-cancel {
          background: #2a2a2a;
          color: #e5e5e5;
          border: 1px solid #444;
        }

        .btn-cancel:hover {
          background: #333;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #444;
          color: #666;
          cursor: not-allowed;
        }
      </style>

      <div class="dialog">
        <div class="header">
          <h2>Add Icon</h2>
          <button class="close-button" aria-label="Close">×</button>
        </div>

        <div class="body">
          <div class="search-section">
            <input type="text"
                   class="search-input"
                   id="icon-search"
                   placeholder="Search icons (e.g., dragon, axe, sword...)"
                   value="${this.searchQuery}"
                   aria-label="Search icons">
          </div>

          <div class="categories ${this.searchQuery ? 'hidden' : ''}">
            ${Object.keys(this.iconCategories).map(cat => `
              <button class="category-tab ${cat === this.activeCategory ? 'active' : ''}"
                      data-category="${cat}">
                ${cat}
              </button>
            `).join('')}
          </div>

          ${this.searchQuery ? `<label class="search-results-label">Search results for "${this.searchQuery}" (${this.searchResults.length} icons)</label>` : ''}

          <div class="icon-grid">
            ${this.renderIconGrid()}
          </div>

          <div class="color-section">
            <label class="section-label">Color</label>
            <div class="color-grid">
              ${this.presetColors.map(color => `
                <div class="color-preset ${color === this.selectedColor ? 'selected' : ''}"
                     style="background: ${color};"
                     data-color="${color}"></div>
              `).join('')}
            </div>
            <div class="custom-color">
              <input type="color" id="color-picker" value="${this.selectedColor}">
              <input type="text" id="color-input" value="${this.selectedColor}"
                     placeholder="#RRGGBB" pattern="^#[0-9A-Fa-f]{6}$">
            </div>
          </div>

          <div class="visibility-section">
            <label class="section-label">Visibility</label>
            <div class="visibility-options">
              <div class="visibility-option">
                <input type="radio" id="vis-show" name="visibility" value="${CONFIG.VISIBILITY_SHOW}"
                       ${this.selectedVisibility === CONFIG.VISIBILITY_SHOW ? 'checked' : ''}>
                <label for="vis-show">
                  Show to Players
                  <span class="visibility-badge show">Show</span>
                </label>
              </div>
              <div class="visibility-option">
                <input type="radio" id="vis-secret" name="visibility" value="${CONFIG.VISIBILITY_SECRET}"
                       ${this.selectedVisibility === CONFIG.VISIBILITY_SECRET ? 'checked' : ''}>
                <label for="vis-secret">
                  GM Secret
                  <span class="visibility-badge secret">Secret</span>
                </label>
              </div>
            </div>
          </div>

          <div class="preview-section">
            <div class="preview-icon" style="color: ${this.selectedColor};">
              ${this.selectedIcon ? '⏳' : '?'}
            </div>
            <div class="preview-label">Icon Preview</div>
          </div>
        </div>

        <div class="footer">
          <button class="btn btn-cancel">Cancel</button>
          <button class="btn btn-primary" id="save-btn" ${!this.selectedIcon ? 'disabled' : ''}>
            Save Icon
          </button>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this.loadIconSvgs();
  }

  async loadIconSvgs() {
    // Use IntersectionObserver for lazy loading - only load icons as they scroll into view
    const placeholders = this.shadowRoot.querySelectorAll('.icon-svg-placeholder');

    // Cleanup previous observer if exists
    if (this.iconObserver) {
      this.iconObserver.disconnect();
    }

    const loadIcon = async (placeholder) => {
      const iconName = placeholder.dataset.iconName;
      if (!iconName) return;

      try {
        const svgData = await iconLibrary.loadSvg(iconName);
        // Create an inline SVG element
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', svgData.viewBox);
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.style.fill = 'currentColor';

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', svgData.path);
        svg.appendChild(path);

        // Replace placeholder with SVG
        placeholder.innerHTML = '';
        placeholder.appendChild(svg);
      } catch (error) {
        console.warn(`Failed to load icon ${iconName}:`, error);
        placeholder.textContent = '?';
      }
    };

    // Create IntersectionObserver to load icons as they become visible
    this.iconObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            loadIcon(entry.target);
            this.iconObserver.unobserve(entry.target);
          }
        });
      },
      {
        root: this.shadowRoot.querySelector('.body'),
        rootMargin: '100px', // Start loading slightly before visible
        threshold: 0.01
      }
    );

    // Observe all placeholders
    placeholders.forEach(placeholder => {
      this.iconObserver.observe(placeholder);
    });
  }

  /**
   * Search all icons across all categories
   * @param {string} query - Search query
   * @returns {string[]} Array of matching icon names
   */
  searchIcons(query) {
    if (!query || query.trim() === '') {
      return [];
    }

    const searchTerm = query.toLowerCase().trim();
    const resultsSet = new Set();

    // Search across all categories (icons may appear in multiple categories)
    for (const [category, icons] of Object.entries(this.iconCategories)) {
      for (const iconName of icons) {
        if (iconName.toLowerCase().includes(searchTerm)) {
          resultsSet.add(iconName);
        }
      }
    }

    return Array.from(resultsSet);
  }

  renderIconGrid() {
    let icons;

    // If searching, show search results
    if (this.searchQuery) {
      icons = this.searchResults;
    } else {
      // Show all category icons (SVG rendering is lazy-loaded on scroll)
      icons = this.iconCategories[this.activeCategory] || [];
    }

    if (icons.length === 0) {
      return `<p style="color: ${CONFIG.COLORS.TEXT_MUTED}; text-align: center; padding: 20px;">No icons found</p>`;
    }

    return icons.map(icon => {
      // Format icon name for tooltip: "dragon-head" → "Dragon Head (dragon-head)"
      const formattedName = icon
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      const tooltip = `${formattedName}\nSearch: ${icon}`;

      return `
        <div class="icon-item ${icon === this.selectedIcon ? 'selected' : ''}"
             data-icon="${icon}"
             title="${tooltip}">
          <div class="icon-svg-placeholder" data-icon-name="${icon}">⏳</div>
        </div>
      `;
    }).join('');
  }

  /**
   * Refresh only the icon grid and related UI without re-rendering entire component
   * This prevents the search input from losing focus
   */
  refreshIconGrid() {
    // Update categories visibility
    const categoriesContainer = this.shadowRoot.querySelector('.categories');
    if (categoriesContainer) {
      categoriesContainer.classList.toggle('hidden', !!this.searchQuery);
    }

    // Update or add search results label
    let searchLabel = this.shadowRoot.querySelector('.search-results-label');
    if (this.searchQuery) {
      if (!searchLabel) {
        searchLabel = document.createElement('label');
        searchLabel.className = 'search-results-label';
        const categoriesDiv = this.shadowRoot.querySelector('.categories');
        categoriesDiv.parentNode.insertBefore(searchLabel, categoriesDiv.nextSibling);
      }
      searchLabel.textContent = `Search results for "${this.searchQuery}" (${this.searchResults.length} icons)`;
      searchLabel.style.display = 'block';
    } else if (searchLabel) {
      searchLabel.style.display = 'none';
    }

    // Update icon grid
    const iconGrid = this.shadowRoot.querySelector('.icon-grid');
    if (iconGrid) {
      iconGrid.innerHTML = this.renderIconGrid();
      this.loadIconSvgs();
    }
  }

  attachEventListeners() {
    // Use EventManager helpers for common modal patterns
    this.addEscapeToClose(() => this.close());
    this.addCloseButton('.close-button, .btn-cancel', () => this.close());

    // Click outside to close - but stop propagation on dialog clicks
    const dialog = this.shadowRoot.querySelector('.dialog');
    if (dialog) {
      this.addManagedListener(dialog, 'click', (e) => {
        e.stopPropagation();
      });
    }
    this.addClickOutsideToClose(() => this.close());

    // Search input
    const searchInput = this.shadowRoot.querySelector('#icon-search');
    if (searchInput) {
      this.addManagedListener(searchInput, 'input', (e) => {
        this.searchQuery = e.target.value;
        this.searchResults = this.searchIcons(this.searchQuery);
        this.refreshIconGrid();
      });
    }

    // Use event delegation for category tabs
    const categoriesContainer = this.shadowRoot.querySelector('.categories');
    if (categoriesContainer) {
      this.addDelegatedListener(categoriesContainer, 'click', '.category-tab', (e, tab) => {
        this.activeCategory = tab.dataset.category;
        this.searchQuery = ''; // Clear search when switching categories
        this.searchResults = [];
        this.render();
      });
    }

    // Use event delegation for icon selection
    const iconGrid = this.shadowRoot.querySelector('.icon-grid');
    if (iconGrid) {
      this.addDelegatedListener(iconGrid, 'click', '.icon-item', (e, iconItem) => {
        // Remove previous selection
        this.shadowRoot.querySelectorAll('.icon-item').forEach(i => {
          i.classList.remove('selected');
        });

        // Add selection to clicked item
        iconItem.classList.add('selected');
        this.selectedIcon = iconItem.dataset.icon;

        // Update preview
        this.updatePreview();

        // Enable save button
        const saveBtn = this.shadowRoot.querySelector('#save-btn');
        if (saveBtn) {
          saveBtn.disabled = false;
        }
      });
    }

    // Use event delegation for color presets
    const colorGrid = this.shadowRoot.querySelector('.color-grid');
    if (colorGrid) {
      this.addDelegatedListener(colorGrid, 'click', '.color-preset', (e, preset) => {
        this.selectedColor = preset.dataset.color;
        this.updateColorSelection();
        this.updatePreview();
      });
    }

    // Color picker
    const colorPicker = this.shadowRoot.querySelector('#color-picker');
    if (colorPicker) {
      this.addManagedListener(colorPicker, 'input', (e) => {
        this.selectedColor = e.target.value;
        this.shadowRoot.querySelector('#color-input').value = e.target.value;
        this.updateColorSelection();
        this.updatePreview();
      });
    }

    // Color input
    const colorInput = this.shadowRoot.querySelector('#color-input');
    if (colorInput) {
      this.addManagedListener(colorInput, 'input', (e) => {
        const color = e.target.value;
        if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
          this.selectedColor = color;
          this.shadowRoot.querySelector('#color-picker').value = color;
          this.updateColorSelection();
          this.updatePreview();
        }
      });
    }

    // Visibility options - use delegation on the container
    const visibilityOptions = this.shadowRoot.querySelector('.visibility-options');
    if (visibilityOptions) {
      this.addDelegatedListener(visibilityOptions, 'change', 'input[name="visibility"]', (e, radio) => {
        this.selectedVisibility = radio.value;
      });
    }

    // Save button
    const saveBtn = this.shadowRoot.querySelector('#save-btn');
    if (saveBtn) {
      this.addManagedListener(saveBtn, 'click', () => this.saveIcon());
    }
  }

  updateColorSelection() {
    // Update preset selection
    this.shadowRoot.querySelectorAll('.color-preset').forEach(preset => {
      if (preset.dataset.color === this.selectedColor) {
        preset.classList.add('selected');
      } else {
        preset.classList.remove('selected');
      }
    });
  }

  async updatePreview() {
    const preview = this.shadowRoot.querySelector('.preview-icon');
    if (preview) {
      preview.style.color = this.selectedColor;
      if (this.selectedIcon) {
        try {
          const svgData = await iconLibrary.loadSvg(this.selectedIcon);
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('viewBox', svgData.viewBox);
          svg.setAttribute('width', '50');
          svg.setAttribute('height', '50');
          svg.style.fill = 'currentColor';

          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', svgData.path);
          svg.appendChild(path);

          preview.innerHTML = '';
          preview.appendChild(svg);
        } catch (error) {
          console.warn(`Failed to load preview icon:`, error);
          preview.textContent = '?';
        }
      }
    }
  }

  async open(hexCoord, existingIcon = null) {
    this.hexCoord = hexCoord;
    this.iconData = existingIcon;
    this.editMode = !!existingIcon;

    // Load categories from manifest if not already loaded
    if (!this.categoriesLoaded) {
      try {
        this.iconCategories = await iconLibrary.getCategories();
        this.categoriesLoaded = true;
      } catch (error) {
        console.error('Failed to load icon categories:', error);
        // Use fallback empty categories
        this.iconCategories = {};
      }
    }

    // Reset search state
    this.searchQuery = '';
    this.searchResults = [];

    // Populate form if editing
    if (existingIcon) {
      this.selectedIcon = existingIcon.iconName;
      this.selectedColor = existingIcon.color || CONFIG.ICON_COLOR_PRESETS[3];
      this.selectedVisibility = existingIcon.visibility || CONFIG.VISIBILITY_SHOW;

      // Find which category contains this icon
      for (const [category, icons] of Object.entries(this.iconCategories)) {
        if (icons.includes(existingIcon.iconName)) {
          this.activeCategory = category;
          break;
        }
      }
    } else {
      // Reset to defaults for new icon
      this.selectedIcon = null;
      this.selectedColor = CONFIG.ICON_COLOR_PRESETS[3];
      this.selectedVisibility = CONFIG.VISIBILITY_SHOW;
      this.activeCategory = 'creatures'; // Use lowercase to match manifest
    }

    this.setAttribute('open', '');
    this.render();

    // Update dialog title after render
    const title = this.shadowRoot.querySelector('h2');
    if (title) {
      title.textContent = this.editMode ? 'Edit Icon' : 'Add Icon';
    }
  }

  close() {
    this.removeAttribute('open');
  }

  saveIcon() {
    if (!this.selectedIcon) return;

    let icon;
    if (this.editMode && this.iconData) {
      // Update existing icon
      icon = {
        ...this.iconData,
        iconName: this.selectedIcon,
        color: this.selectedColor,
        visibility: this.selectedVisibility,
        updatedAt: Date.now()
      };
    } else {
      // Create new icon
      // Get current game date for time travel filtering
      const gameDate = window.currentSession?.calendar?.currentDate
        ? { ...window.currentSession.calendar.currentDate }
        : { month: 1, year: 1, day: 1 }; // Fallback to Day 1

      icon = {
        id: 'icon-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11),
        iconName: this.selectedIcon,
        color: this.selectedColor,
        visibility: this.selectedVisibility,
        gameDate: gameDate,  // Track when icon was created for time travel
        createdAt: Date.now()
      };
    }

    // Dispatch event using HexEvents system (properly bubbles and composes through Shadow DOM)
    this.dispatchEvent(createHexEvent(HexEvents.ICON_SAVED, {
      hexCoord: this.hexCoord,
      icon: icon,
      isEdit: this.editMode
    }));

    this.close();
  }
}

customElements.define('icon-picker', IconPicker);